import React, { useRef, useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Badge,
  FormControl,
  InputGroup,
} from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { FaFileAlt, FaFilePdf, FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { BiFileBlank } from 'react-icons/bi';
import { useHandoverDocuments } from './useHandoverDocuments';
import '../../../../css/DppWidget.css';

const HandoverDocumentsWidget = ({ submodel }) => {
  const intl = useIntl();
  const { allDocuments, hasDocuments } = useHandoverDocuments(submodel);

  const [searchQuery, setSearchQuery] = useState('');
  const scrollContainerRef = useRef(null);

  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return allDocuments;

    const query = searchQuery.toLowerCase();
    return allDocuments.filter(doc =>
      doc.title.toLowerCase().includes(query) ||
      (doc.subtitle && doc.subtitle.toLowerCase().includes(query)) ||
      (doc.organizationOfficial && doc.organizationOfficial.toLowerCase().includes(query))
    );
  }, [allDocuments, searchQuery]);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -340, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 340, behavior: 'smooth' });
  };

  if (!hasDocuments) return null;
  const hasResults = filteredDocuments.length > 0;

  return (
    <div className="dpp-widget">
      <div className="p-4 p-md-5">
        {/* Header + Search */}
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-4 mb-4 pb-4">
          <h2 className="dpp-widget-title mb-0">
            <FormattedMessage id="handoverDocumentation.title" defaultMessage="Document Browser" />
          </h2>

          <InputGroup style={{ width: '100%', maxWidth: '400px' }}>
            <FormControl
              type="search"
              enterKeyHint="search"
              placeholder={intl.formatMessage({
                id: 'handoverDocumentation.searchPlaceholder',
                defaultMessage: 'Search documents...',
              })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                height: '55px',
                borderRadius: '10px',
                border: '1px solid #d8d8d8',
                color: '#003E81',
                padding: '10px 40px 10px 15px',
              }}
            />
            <FaSearch style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', color: '#003E81', zIndex: 5 }} />
          </InputGroup>
        </div>

        {/* No results */}
        {searchQuery && !hasResults && (
          <div className="text-center text-muted py-5">
            <FormattedMessage
              id="handoverDocumentation.noResults"
              defaultMessage='No documents found matching “{query}”'
              values={{ query: <strong>{searchQuery}</strong> }}
            />
          </div>
        )}

        {/* Single horizontal scrollable row */}
        {hasResults && (
          <div className="position-relative">
            {/* Arrows */}
            <button onClick={scrollLeft} className="dpp-scroll-button left" style={{ top: '45%', color: '#003E81' }} aria-label="Scroll left">
              <FaChevronLeft />
            </button>
            <button onClick={scrollRight} className="dpp-scroll-button right" style={{ top: '45%', color: '#003E81' }} aria-label="Scroll right">
              <FaChevronRight />
            </button>

            <div
              className="dpp-documents-scroll-container ps-5 pe-5"
              ref={scrollContainerRef}
            >
              <Row className="g-4 flex-nowrap">
                {filteredDocuments.map((doc, idx) => {
                  const hasPdf = doc.hasPdf;

                  return (
                    <Col key={idx} style={{ minWidth: '340px', maxWidth: '380px' }}>
                      <Card
                        className={`h-100 dpp-widget-card bg-white transition-all}`}
                        style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '20px',
                        }}
                      >
                        <Card.Body className="d-flex flex-column p-4">
                          <div className="d-flex align-items-start mb-3">
                            <div className="flex-shrink-0 me-4">
                              {hasPdf ? (
                                <FaFileAlt style={{ fontSize: '48px', color: '#003E81' }} />
                              ) : (
                                <BiFileBlank style={{ fontSize: '48px', color: '#bbb' }} />
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <Card.Title as="h5" className="mb-3 fw-bold text-break" style={{ fontSize: '1.3rem' }}>
                                {doc.title}
                              </Card.Title>

                              {doc.subtitle && (
                                <Card.Text className="text-muted small mb-1">{doc.subtitle}</Card.Text>
                              )}
                              {doc.organizationOfficial && (
                                <Card.Text className="text-muted small mb-1">{doc.organizationOfficial}</Card.Text>
                              )}
                              {doc.date && (
                                <Card.Text className="text-muted small mb-1">
                                  {new Date(doc.date).toLocaleDateString(intl.locale)}
                                </Card.Text>
                              )}
                              <Card.Text className="text-muted small mb-1">
                                {doc.language.toUpperCase()}
                              </Card.Text>

                              <div className="mt-4 d-flex flex-wrap gap-2">
                                {doc.digitalFiles.length > 0 ? (
                                  doc.digitalFiles.map((file, fileIdx) => (
                                    <h5 key={fileIdx}>
                                    <Badge
                                      key={fileIdx}
                                      bg={file.hasUrl ? "" : "secondary"}
                                      pill
                                      className="py-2 px-4"
                                      onClick={() => file.hasUrl && window.open(file.url, '_blank')}
                                      style={{ cursor: file.hasUrl ? 'pointer' : 'default', color: '#003E81', backgroundColor: '#F0F5FA' }}
                                    >
                                      <FaFilePdf className="me-2" />
                                      <FormattedMessage
                                        id="handoverDocumentation.viewPdf"
                                        defaultMessage="PDF"
                                      />
                                      {doc.digitalFiles.length > 1 && ` (${fileIdx + 1})`}
                                    </Badge>
                                  </h5>
                                  ))
                                ) : (
                                  <Badge bg="secondary" pill className="py-2 px-4">
                                    <FormattedMessage
                                      id="handoverDocumentation.noFile"
                                      defaultMessage="No file available"
                                    />
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HandoverDocumentsWidget;